import React, { ChangeEvent, MouseEvent, useState } from 'react';
import Select, { components } from 'react-select'
import { IPlayer, IGameStatus } from '../../interfaces';
import { fetchGameStatus } from '../../features/player/gameStatusSlice'
import { postReady, postRole, kickFromGame } from '../../features/player/gameSlice'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import Modal from 'react-modal';
import { ChevronDown as ChevronDownIcon} from '@carbon/icons-react'

interface ParticipantProps {
    player: IPlayer;
    currentGameId: string;
    currentGameStatus: IGameStatus;
    disabled: boolean;
    sortedRoles: string[];
}

Modal.setAppElement('#root');

const Participant: React.FC<ParticipantProps> = ({ player, currentGameId, currentGameStatus, disabled, sortedRoles }) => {
    const customStyles = {
        content: {
          background: '#323544',
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          padding: '0',
          border: 'none',
          width: '480px',
          transform: 'translate(-50%, -50%)',
        }
      };

    const dispatch: any = useAppDispatch();

    const user = useAppSelector((state) => state.auth.userId)

    const [roleError, setRoleError] = useState<string>('')
    const [kickPlayerModalIsOpen, setKickPlayerModalIsOpen] = useState<boolean>(false);
    const [playerToKick, setPlayerToKick] = useState<string>('');
    const [playerStatusLoading, setPlayerStatusLoading] = useState<boolean>(false);
    const [kickGameLoading, setKickGameLoading] = useState<boolean>(false);

    const CustomDropdownIndicator = (props: any) => {
        return (
          <components.DropdownIndicator {...props}>
            <ChevronDownIcon color="#161616"/>
          </components.DropdownIndicator>
        );
      };

    const dropdownStyles = {
        option: (provided: any, { isFocused, isSelected } : { isFocused: any, isSelected: any }) => ({
            ...provided,
            backgroundColor: isFocused ? "#FFFFFF" : "#FFFFFF",
            fontSize: 14,
            color: "#161616",
            ":hover": {
              backgroundColor: "#878FA1",
              color: "#FFFFFF"
            }
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            padding: 0,
            color: '#161616',
            backgroundColor: "#FFFFFF",
            width: 176,
            fontSize: 14,
            marginLeft: 8
        }),
        container:  (provided: any, state: any) => ({
            ...provided,
            border: "none",
            width: 176,
            height: 50,
            borderBottom: "1px solid #161616"
        }),
        control:  (provided: any, state: any) => ({
            ...provided,
            backgroundColor: "#FFFFFF",
            color: "#161616",
            borderRadius: 0,
            border: "none",
            boxShadow: 0,
            width: 176,
            height: 49
        }),
        placeholder:  (provided: any, state: any) => ({
            ...provided,
            color: "#161616",
            fontSize: 14
        }),
        indicatorSeparator:  (provided: any, state: any) => ({
            ...provided,
            display: "none"
        }),
        menu:  (provided: any, state: any) => ({
            ...provided,
            width: 176,
            color: '#161616',
            backgroundColor: "#FFFFFF",
            top: 50,
            zIndex: 3000
        }),
        input:  (provided: any, state: any) => ({
            ...provided,
            marginTop: 5,
            height: 37
        }),
        menuList:  (provided: any, state: any) => ({
            ...provided,
            color: '#161616',
            backgroundColor: "#FFFFFF",
            paddingTop: 0,
            paddingBottom: 0,
            zIndex: 3000
        }),
        indicatorContainer:  (provided: any, state: any) => ({
            ...provided,
            color: '#161616'
        })
    }

    const options: any[] = [];

    sortedRoles.map((role: string) => (
        options.push({ value: role, label: role })
    ))

    const openKickPlayerModal = () => {
        setKickPlayerModalIsOpen(true);
    }
    
    const closeKickPlayerModal = () => {
        setKickPlayerModalIsOpen(false);
    }

    const kickGame = async (ev: MouseEvent) => {

        if (kickGameLoading) {
            return;
        }

        setKickGameLoading(true)

        const target = ev.currentTarget as HTMLElement;

        if (target) {
            let readyResponse = await dispatch(kickFromGame({gameId: currentGameId, playerId: player.userID}))
            if (readyResponse.error !== undefined) {
                if (readyResponse.error.message === 'Rejected') {
                    setRoleError('role-error')
                }
            } else {
                dispatch(fetchGameStatus(currentGameId))
                closeKickPlayerModal()
            }
            setKickGameLoading(false)
        }
    }

    const clickKickGame = (ev: MouseEvent) => {
        const target = ev.currentTarget as HTMLElement;

        if (target) {
            setPlayerToKick(target.dataset.id!)
            openKickPlayerModal()
        }
    }

    async function togglePlayerStatus (ev: ChangeEvent) {
        if (playerStatusLoading) {
            return;
        }

        setPlayerStatusLoading(true)

        const target = ev.currentTarget as HTMLElement;
        if (target) {
            console.log(target);
            let readyResponse = await dispatch(postReady({gameId: currentGameId, ready: !player.ready, playerId: player.userID}))

            if (readyResponse.error !== undefined) {
                if (readyResponse.error.message === 'Rejected') {
                    setRoleError('role-error')
                }
            } else {
                dispatch(fetchGameStatus(currentGameId))
            }
            setPlayerStatusLoading(false)
        }
    }

    const handleChange = async (selectedOption: any) => {
        // set role on server
        await dispatch(postRole({gameId: currentGameId, role: selectedOption.value, playerId: player.userID }))

        dispatch(fetchGameStatus(currentGameId))
        setRoleError('')
    }

    return ( <div className="participants-row">
        <div className="participant-name">{player.userID}</div>
        <div className={`participant-role ${roleError}`}>
            <div className={`role-needed-error ${roleError}`}>You must select a role before changing your scenario status to "Ready!"</div>
            { disabled ? <div className="participant-select-value">{player.role}</div> : 
                <Select
                    value={{label: player.role, value: player.role}}
                    onChange={handleChange}
                    components={{ DropdownIndicator: CustomDropdownIndicator }}
                    options={options}
                    styles={dropdownStyles}
                    className={'role-select'}
                    data-id={`role-${player.userID}`}
                /> 
            }
        </div>
        { player.role !== null && <>
        { !disabled &&
        <div className="checkbox-wrapper-9 participant-toggle">
            <input id={`ready-${player.userID}`} className="tgl tgl-ios" type="checkbox" value={player.ready.toString()} checked={player.ready} onChange={togglePlayerStatus} disabled={disabled || playerStatusLoading} /><label htmlFor={`ready-${player.userID}`} className="tgl-btn"></label>
        </div>
        }
        <div className="participant-status-update">{player.ready ? 'Ready!' : 'Not Ready'}</div>
        </>
        }
        { player.userID !== user && currentGameStatus.host === user ? <img className="participant-trash" src="img/trash-black.svg" onClick={clickKickGame} data-id={player.userID} alt="trash" /> : <></> }

            <Modal
                isOpen={kickPlayerModalIsOpen}
                onRequestClose={closeKickPlayerModal}
                style={customStyles}
                contentLabel="Kick Player Modal"
            >
                <div className="new-modal-header">Kick Player? <img src="/img/light-grey-x.svg" className="new-modal-padding-close" alt="x" onClick={closeKickPlayerModal}></img></div>
                
                <div className="new-modal-text">Are you sure you want to kick <span className="modal-scenario-name">{playerToKick}</span> from the game?</div>

                <button onClick={closeKickPlayerModal} className="new-cancel-btn"><span>Cancel</span></button>
                <button onClick={kickGame} className={`new-confirm-btn ${kickGameLoading ? 'start-loading' : ''}`}>
                    {kickGameLoading ? <div className="game-save-loader-25"></div> : <></> }
                    <span>Remove Player</span>
                </button>
            </Modal>
        </div>
    )

}

export default Participant;